import type { SagaIterator } from 'redux-saga';
import { call, cancelled, put, takeLatest } from 'redux-saga/effects';

import { getLogger } from 'app-components/utils/core';

import type { TaskInstance } from './taskSchemas';
import * as actions from './tasksActions';
import tasksApi from './tasksApi';

const log = getLogger('flux/tasks/tasksSagas');

function* getTasks(action: ReturnType<typeof actions.getTasks>): SagaIterator {
  const controller = new AbortController();

  try {
    const tasks: TaskInstance[] = yield call(tasksApi.fetch, controller.signal);
    yield put(actions.getTasksSuccess({ resources: tasks }, action.meta));
  } catch (error: unknown) {
    if (controller.signal.aborted) return;
    log.warn('Failed to read the task collection; rendering without it', error);
    yield put(actions.getTasksFailure(error instanceof Error ? error.message : String(error), action.meta));
  } finally {
    if (yield cancelled()) controller.abort();
  }
}

type GetTasksWatcherAction = ReturnType<typeof actions.getTasks> | ReturnType<typeof actions.clearTasks>;

function* getTasksOrClear(action: GetTasksWatcherAction): SagaIterator {
  if (actions.getTasks.match(action)) yield* getTasks(action);
}

export function* getTasksActionWatcher(): SagaIterator {
  // Clearing the local collection supersedes a pending read just as a newer read does.
  yield takeLatest([actions.getTasks.type, actions.clearTasks.type], getTasksOrClear);
}

export function* tasksDumpUpdater(resources: unknown[] = []): SagaIterator {
  yield put(actions.reconcileTasks({ resources }, { context: 'BUMP_CHANGE_DUMP' }));
}

export function* tasksNudgeUpdater(): SagaIterator {
  yield put(actions.getTasks(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
}

export default [getTasksActionWatcher];
